/**
 * 批量生成确认弹窗 ×2（v1.4.84 新建，对齐用户参考图一/图二）：
 * 画布脚本节点顶部栏「批量生成分镜 / 批量生视频」从点击直通改为先弹确认面板——
 * 1) BatchStoryboardModal「分镜批量生图」（图一）：镜头勾选列表默认全选（蓝色 checkbox），
 *    行点击分区（v1.4.87 折叠面板）：勾选区 = checkbox + 「分镜N」（点击勾选/取消）；文本区 =
 *    该镜画面描述语句（shot.description，为空回落分镜提示词），点击展开/折叠该镜详情——
 *    折叠态看不到提示词；展开内容与「分」字左对齐、同一卡片内直排（不再用内嵌黑框）：
 *    「资产图锚定：」逐行「类别 名称的参考图是 @名称」（@名蓝色高亮，hover 弹与最终合成
 *    提示词面板一致的 264px 资产图预览卡；未出图资产 @名 置灰）+ 分镜提示词全文
 *    （@/资产名同样高亮可 hover；经 AssetMentionText 只读提及组件渲染）；
 *    底栏 = ☑已选 N/N + 模型/画质/分辨率/自适应/比例 五下拉 + 白色「确认并创建生成队列 (N)」；
 *    确认回调携带所选镜头 + 生图参数（由 useScriptBatchGenerator 透传写入分镜图节点 data）。
 * 2) BatchVideoModal「批量生视频」（图二）：行 = 已出图分镜（checkbox + 镜头N + 行尾时长下拉
 *    4s/8s/12s，默认 8s；v1.4.94 行样式对齐图一折叠面板同构：摘要点击展开/折叠提示词全文，
 *    固定高度窗口超限滚动；v1.4.95 行摘要 = 画面描述（同图一语义），展开区 = 视频运动提示词
 *    经 AssetMentionText 渲染 @资产高亮 + hover 缩略图预览卡）；
 *    底栏 = ☑已选 N/N + 视频模型下拉 + Audio 开关 + 分辨率下拉(480P/720P/1080P) +
 *    白色「确认并创建视频生成队列 (N)」；确认回调携带所选分镜图节点 id + 每镜时长 +
 *    运动提示词映射（写入视频节点 prompt 参与图生视频）+ 视频参数。
 * 样式对齐参考图：深色面板（#1a1a1c）+ 标题行（左标题右 X）+ 全宽信息条（参考图说明文案）+
 * 镜头行卡片 + 紧凑参数下拉（icon + 当前值 + ChevronDown，遮罩统一收起，同 AssetGenOptionsBar
 * 视觉语言）+ 白色圆角主按钮；由画布 ScriptNodeToolbar 挂载，
 * ModalShell 经 createPortal 挂 document.body（工具条位于 InverseScaleBox 反向缩放坐标系内，
 * 裸 fixed 会被 transform 祖先捕获为 containing block，portal 才能真正全屏居中；
 * 层级对齐 FinalPromptAllModal 的 z-[300]）；
 * v1.4.93 参数菜单改 createPortal+fixed 于 body 渲染（ParamDropdown 统一承载，含行尾时长）：
 * 面板 overflow-hidden / 列表 overflow-y-auto 会裁掉 absolute 菜单溢出部分（面板较矮时
 * 比例菜单上弹显示不全），portal 后按按钮视口坐标定位、空间不足自动向下翻、再不足限高滚动。
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Check, ChevronDown, Cpu, Gem, Info, Monitor, RectangleHorizontal, Scan, Volume2, X,
} from 'lucide-react';
import { useModelStore, FALLBACK_IMAGE_MODELS } from '../../stores/modelStore';
import { IMAGE_ASPECT_RATIOS } from '../flow/nodes/parts/ParamSelectors';
import { collectShotMentionedAssets, SCRIPT_ASSET_KIND_LABELS, ScriptAsset, Shot } from '../../types/scriptNode';
import { AssetMentionText } from './AssetMentionText';

/** v1.4.84 分镜批量生图参数（弹窗底栏收集；「自适应」仅 UI 展示，无对应生成维度不透传） */
export interface BatchImageOptions {
  modelId: string;
  /** 画质：standard 走模型默认不显式传，high 显式传（对齐 AssetGenOptions 契约） */
  quality?: string;
  resolution: string;
  aspectRatio: string;
}

/** v1.4.84 批量生视频参数（弹窗底栏收集 + 每镜时长/运动提示词，key = 分镜图节点 id） */
export interface BatchVideoOptions {
  modelId: string;
  generateAudio: boolean;
  resolution: string;
  /** 每镜时长（秒） */
  durations: Record<string, number>;
  /** 每镜视频运动提示词（写入视频节点 prompt 参与图生视频） */
  prompts: Record<string, string>;
}

/** 批量生视频行数据（ScriptNodeToolbar 构建：已出图分镜图节点 ↔ 对应镜头） */
export interface BatchVideoItem {
  /** 已出图的分镜图节点 id（确认时回传，generateBatchVideos 按 nodeIds 过滤） */
  nodeId: string;
  /** 展示序号（对应镜头 index；失配时 0） */
  shotIndex: number;
  /** v1.4.95 画面描述（行摘要展示，对齐批量生图行语义；空时回退运动提示词摘要） */
  description: string;
  /** 最终提示词中的视频运动提示词（展开区展示，确认后写入视频节点 prompt） */
  motionPrompt: string;
  /** v1.4.95 脚本资产（展开区 AssetMentionText 渲染 @资产 蓝色高亮 + hover 缩略图预览卡） */
  assets: ScriptAsset[];
}

/** 参数下拉（同 AssetGenOptionsBar 的 ParamDropdown 视觉：icon + 当前值 + ChevronDown）；
 * v1.4.93 菜单改 createPortal+fixed 于 body 渲染（对齐 ShotsTable v1.4.29 行菜单先例）：
 * 弹窗面板 overflow-hidden（圆角裁剪）与镜头列表 overflow-y-auto 会把 absolute 菜单溢出
 * 部分一并裁掉（面板较矮时底栏参数菜单上弹被裁 / 行尾时长菜单被滚动区裁），portal 后按
 * 按钮视口坐标定位：默认向上弹，上方放不下且下方更宽裕时向下翻，目标侧仍不足时限高滚动 */
const ParamDropdown: React.FC<{
  icon?: React.ReactNode;
  label: string;
  open: boolean;
  onToggle: () => void;
  menuWidth?: number;
  /** 菜单对齐边（行尾时长下拉右对齐；默认左对齐） */
  align?: 'left' | 'right';
  /** 紧凑按钮（行尾时长下拉 h-7 px-2；默认 h-8 px-2.5） */
  compact?: boolean;
  children: React.ReactNode;
}> = ({ icon, label, open, onToggle, menuWidth = 180, align = 'left', compact = false, children }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // 菜单定位：open 后先隐形渲染测全高，layout effect（绘制前）算方向/限高并落位，无闪烁
  const [place, setPlace] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const fullH = menuRef.current?.offsetHeight ?? 0;
    const spaceUp = r.top - 8;
    const spaceDown = window.innerHeight - r.bottom - 8;
    // 优先向上弹（底栏语义，保持原方向）；上方放不下且下方更宽裕时向下翻
    const flipDown = spaceUp < fullH && spaceDown > spaceUp;
    const left = Math.max(
      8,
      Math.min(align === 'left' ? r.left : r.right - menuWidth, window.innerWidth - menuWidth - 8),
    );
    setPlace(
      flipDown
        ? { left, top: r.bottom + 8, maxHeight: Math.max(spaceDown, 120) }
        : { left, bottom: window.innerHeight - r.top + 8, maxHeight: Math.max(spaceUp, 120) },
    );
  }, [open, menuWidth, align]);
  // 打开期间滚动/缩放即收起（fixed 菜单不随内容滚动，capture 捕获列表内部滚动容器；
  // 菜单/按钮自身滚动与点击不算——菜单限高滚动不可误关）
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      onToggle();
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, onToggle]);
  const menu = open ? (
    <div
      ref={menuRef}
      className="fixed z-[310] py-1.5 rounded-xl bg-[#262626] border border-white/10 shadow-xl overflow-y-auto"
      style={
        place
          ? { width: menuWidth, left: place.left, top: place.top, bottom: place.bottom, maxHeight: place.maxHeight }
          : { width: menuWidth, left: 0, top: 0, visibility: 'hidden' }
      }
    >
      {children}
    </div>
  ) : null;
  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-1.5 ${compact ? 'h-7 px-2 rounded-md' : 'h-8 px-2.5 rounded-lg'} border text-[12px] transition-colors ${
          open
            ? 'bg-white/10 border-white/25 text-white/95'
            : 'bg-white/[0.06] border-white/10 text-white/70 hover:bg-white/10 hover:text-white/95'
        }`}
      >
        {icon && <span className="shrink-0 text-white/45">{icon}</span>}
        <span className="max-w-[128px] truncate">{label}</span>
        <ChevronDown size={12} className={`shrink-0 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
};

/** 下拉菜单行（当前值左侧对勾高亮） */
const ParamMenuItem: React.FC<{
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] transition-colors ${
      active ? 'text-white/95 bg-white/[0.08]' : 'text-white/65 hover:bg-white/10 hover:text-white/90'
    }`}
  >
    <Check size={12} className={`shrink-0 ${active ? 'text-blue-400' : 'invisible'}`} />
    <span className="flex-1 min-w-0 truncate">{children}</span>
  </button>
);

/** 全宽信息条（参考图：ⓘ + 说明文案，弹窗标题行下方） */
const InfoBar: React.FC<{ text: string }> = ({ text }) => (
  <div className="shrink-0 flex items-start gap-2 px-6 py-2.5 bg-white/[0.04] border-y border-white/[0.04]">
    <Info size={13} className="shrink-0 mt-[2px] text-blue-300/70" />
    <span className="text-[12px] leading-relaxed text-white/55">{text}</span>
  </div>
);

/** 底栏左侧「已选 N/N」（含全选切换 checkbox，对齐参考图「☑已选 2/2」） */
const SelectedCounter: React.FC<{ total: number; selectedCount: number; onToggleAll: () => void }> = ({
  total,
  selectedCount,
  onToggleAll,
}) => (
  <label className="flex items-center gap-1.5 cursor-pointer select-none shrink-0">
    <input
      type="checkbox"
      className="h-3.5 w-3.5 accent-blue-500"
      checked={total > 0 && selectedCount === total}
      disabled={total === 0}
      onChange={onToggleAll}
    />
    <span className="text-[12px] text-white/70 whitespace-nowrap">已选 {selectedCount}/{total}</span>
  </label>
);

/** 底栏右侧白色主按钮（参考图「确认并创建生成队列 (N)」规格） */
const ConfirmButton: React.FC<{ disabled: boolean; onClick: () => void; children: React.ReactNode }> = ({
  disabled,
  onClick,
  children,
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    title={disabled ? '请先勾选镜头' : undefined}
    className="shrink-0 px-4 py-1.5 rounded-lg bg-white text-black text-[12px] font-medium hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
  >
    {children}
  </button>
);

/** 弹窗骨架：遮罩（点击关）+ 深色面板（标题行 + 信息条 + children[列表/底栏]） */
function ModalShell({ title, infoText, onClose, children }: {
  title: string;
  infoText: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  // v1.4.84 portal 到 body：画布工具条在 InverseScaleBox（transform）坐标系内，
  // 裸 fixed 的 containing block 会退化为该祖先，portal 后才能真正覆盖全屏
  return createPortal(
    <div className="fixed inset-0 z-[300] overflow-hidden">
      <div className="flex h-full w-full items-center justify-center bg-black/60" onClick={onClose}>
        <div
          className="w-[940px] max-w-[94%] max-h-[86%] flex flex-col rounded-xl bg-[#1a1a1c] border border-white/[0.08] shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 flex items-center justify-between pl-6 pr-4 pt-4 pb-3">
            <div className="text-[15px] font-medium text-white/90">{title}</div>
            <button
              type="button"
              onClick={onClose}
              title="关闭"
              className="p-1 rounded-md text-white/45 hover:text-white/90 hover:bg-white/10 transition-colors"
            >
              <X size={15} />
            </button>
          </div>
          <InfoBar text={infoText} />
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** 镜头行通用骨架（v1.4.87 点击分区）：勾选区 = label（checkbox + 镜头N/分镜N，点击勾选/取消）；
 * 文本区 = 摘要（line-clamp-2，expandable 时点击展开/折叠 children，折叠态看不到提示词）；
 * 行尾扩展区（时长下拉）独立，不与勾选/展开相互干扰。children 渲染在文本区下方、同一卡片内，
 * 展开内容 pl-9（36px = px-3 12 + checkbox 14 + gap 10）与标签首字（「分」字）左对齐 */
function ShotRow({ checked, onToggle, index, indexLabel = '镜头', text, trailing, expandable, expanded, onToggleExpand, children }: {
  checked: boolean;
  onToggle: () => void;
  index: number;
  /** 标签文案（分镜批量生图传「分镜」，批量生视频用默认「镜头」） */
  indexLabel?: string;
  text: string;
  trailing?: React.ReactNode;
  /** 文本区是否可点击展开（批量生视频行不展开） */
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-lg border transition-colors ${
        checked ? 'bg-[#242428] border-white/20' : 'bg-[#222226] border-white/[0.06] hover:bg-[#26262a]'
      }`}
    >
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <label className="flex items-center gap-2.5 cursor-pointer select-none shrink-0">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-blue-500 shrink-0"
            checked={checked}
            onChange={onToggle}
          />
          <span className="text-[13px] font-medium text-white/90 whitespace-nowrap">{indexLabel}{index}</span>
        </label>
        <span
          className={`min-w-0 flex-1 text-[12px] text-white/45 leading-relaxed line-clamp-2 transition-colors ${
            expandable ? 'cursor-pointer hover:text-white/70' : ''
          }`}
          title={expandable ? (expanded ? '点击折叠' : '点击展开提示词') : text}
          onClick={expandable ? onToggleExpand : undefined}
        >
          {text}
        </span>
        {trailing && <span className="shrink-0 flex items-center">{trailing}</span>}
      </div>
      {children}
    </div>
  );
}

/** 当前选型不在列表（被隐藏/下架/晚到）时回落第一个 */
function useFallbackModelId(models: { id: string }[], modelId: string, setModelId: (id: string) => void) {
  useEffect(() => {
    if (models.length > 0 && !models.some((m) => m.id === modelId)) {
      setModelId(models[0].id);
    }
  }, [models, modelId, setModelId]);
}

/** 模型列表拉取（挂载未拉取则拉取一次，store 单飞） */
function useEnsureModels() {
  useEffect(() => {
    if (useModelStore.getState().models.length === 0) {
      void useModelStore.getState().loadModels();
    }
  }, []);
}

/** 图像/视频通用下拉组内部共享的打开态管理：任一时刻至多一个菜单展开，点遮罩统一收起 */
function useDropdownMenu() {
  const [menu, setMenu] = useState('');
  const menuMask = menu ? (
    <div className="fixed inset-0 z-10" onClick={() => setMenu('')} />
  ) : null;
  return { menu, setMenu, menuMask };
}

/* ------------------------------------------------------------------ */
/* 图一「分镜批量生图」确认弹窗                                         */
/* ------------------------------------------------------------------ */

interface BatchStoryboardModalProps {
  /** 可参与批量生图的镜头（已按就绪度过滤） */
  shots: Shot[];
  /** v1.4.85 脚本资产（锚定判定数据源：行内默认展示各镜头「资产图锚定」段） */
  assets: ScriptAsset[];
  onClose: () => void;
  /** 确认创建：携带所选镜头（保持脚本内原顺序）+ 生图参数 */
  onConfirm: (selected: Shot[], options: BatchImageOptions) => void;
}

export function BatchStoryboardModal({ shots, assets, onClose, onConfirm }: BatchStoryboardModalProps) {
  // 勾选集合存 shot id，默认全选（对齐参考图「已选 2/2」）
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(shots.map((s) => s.id)));
  // v1.4.87 折叠面板：当前展开详情的镜头 id（单开互斥；null = 全部折叠，折叠态看不到提示词）
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [modelId, setModelId] = useState('');
  const [quality, setQuality] = useState('standard');
  const [resolution, setResolution] = useState('2k');
  const [adaptive, setAdaptive] = useState('自适应');
  const [ratio, setRatio] = useState('16:9');

  // 生图模型列表（txt2img + 供应商可见性，兜底常量；同 AssetGenOptionsBar 口径）
  useEnsureModels();
  const storeModels = useModelStore((s) => s.models);
  const providerVisibility = useModelStore((s) => s.providerVisibility);
  const imageModels = useMemo(() => {
    const list = storeModels.filter(
      (m) => m.supportedModes.includes('txt2img') && providerVisibility[m.backend] !== false,
    );
    return list.length > 0 ? list : FALLBACK_IMAGE_MODELS.filter((m) => m.supportedModes.includes('txt2img'));
  }, [storeModels, providerVisibility]);
  useFallbackModelId(imageModels, modelId, setModelId);
  const genModel = imageModels.find((m) => m.id === modelId) ?? imageModels[0];

  const { menu, setMenu, menuMask } = useDropdownMenu();

  // 未合成单镜提示词的镜头（将以画面描述兜底）——对齐参考图「画面描述较为简略」提示行
  const descOnlyLabel = shots
    .filter((s) => !s.prompt.trim())
    .map((s) => s.index)
    .join('、');

  const selectedCount = shots.filter((s) => selectedIds.has(s.id)).length;

  const toggleShot = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(selectedCount === shots.length ? new Set() : new Set(shots.map((s) => s.id)));
  };

  const handleConfirm = () => {
    if (selectedCount === 0) return;
    onConfirm(shots.filter((s) => selectedIds.has(s.id)), {
      modelId: modelId || genModel?.id || '',
      quality: quality === 'high' ? 'high' : undefined,
      resolution,
      aspectRatio: ratio,
    });
  };

  return (
    <ModalShell
      title="分镜批量生图"
      infoText="会优先使用已生成的角色、场景和道具参考图，以画面更贴合分镜内容。"
      onClose={onClose}
    >
      {/* 镜头勾选列表（v1.4.87：行文本 = 画面描述语句，点击文本展开/折叠提示词详情） */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-3 pb-3 flex flex-col gap-2">
        {descOnlyLabel && (
          <div className="text-[12px] text-amber-400/80">
            当前镜头{descOnlyLabel}分镜提示词较为简略，将自动拼接全局画面风格后生成
          </div>
        )}
        {shots.map((shot) => {
          // v1.4.87 资产图锚定 + 分镜提示词全文收纳进展开块：折叠态只有画面描述语句，
          // 点击展开后同一卡片内直排（与「分」字左对齐，无内嵌黑框）。
          // 锚定行「类别 名称的参考图是 @名称」与提示词全文均经 AssetMentionText 渲染：
          // @资产名 蓝色高亮、hover 弹与最终合成提示词面板一致的 264px 资产图预览卡；
          // 未出图资产 @名 置灰（别名同置灰）标注「不会注入参考图」。
          const mentioned = collectShotMentionedAssets(shot, assets);
          const mutedNames = mentioned.filter((a) => !a.imageUrl).map((a) => a.name);
          const expanded = expandedId === shot.id;
          return (
            <ShotRow
              key={shot.id}
              checked={selectedIds.has(shot.id)}
              onToggle={() => toggleShot(shot.id)}
              index={shot.index}
              indexLabel="分镜"
              text={shot.description.trim() || shot.prompt.trim() || '暂无画面描述'}
              expandable
              expanded={expanded}
              onToggleExpand={() => setExpandedId(expanded ? null : shot.id)}
            >
              {expanded && (
                <div className="px-3 pb-2.5 pl-9 flex flex-col gap-2">
                  {mentioned.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <div className="text-[12px] font-medium text-white/85">资产图锚定：</div>
                      {mentioned.map((a) => (
                        <AssetMentionText
                          key={a.id}
                          // enableBareNames=false：锚定行同词「裸名+@名」各出现一次，全高亮会同词双高亮
                          text={`${SCRIPT_ASSET_KIND_LABELS[a.kind] ?? '资产'} ${a.name}的参考图是 @${a.name}`}
                          assets={assets}
                          mutedNames={mutedNames}
                          enableBareNames={false}
                          className="text-[12px] leading-relaxed text-white/70"
                        />
                      ))}
                    </div>
                  )}
                  <AssetMentionText
                    text={shot.prompt.trim() || '暂无分镜提示词，将按画面描述拼接全局画面风格后生成'}
                    assets={assets}
                    mutedNames={mutedNames}
                    className="text-[12px] leading-relaxed text-white/60"
                  />
                </div>
              )}
            </ShotRow>
          );
        })}
        {shots.length === 0 && (
          <div className="py-12 text-center text-[13px] text-white/35">暂无可生成的镜头</div>
        )}
      </div>
      {/* 底栏：已选计数 + 模型/画质/分辨率/自适应/比例 五下拉 + 确认按钮（对齐参考图一；flex-wrap 兜底防小屏重叠） */}
      <div className="shrink-0 flex flex-wrap items-center gap-x-3 gap-y-2 px-6 py-3 bg-[#1c1c1e] border-t border-white/[0.06]">
        <SelectedCounter total={shots.length} selectedCount={selectedCount} onToggleAll={toggleAll} />
        <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
          {menuMask}
          <ParamDropdown
            icon={<Cpu size={12} />}
            label={genModel?.name ?? '默认模型'}
            open={menu === 'model'}
            onToggle={() => setMenu(menu === 'model' ? '' : 'model')}
            menuWidth={216}
          >
            {imageModels.map((m) => (
              <ParamMenuItem key={m.id} active={m.id === modelId} onClick={() => { setModelId(m.id); setMenu(''); }}>
                {m.name}
              </ParamMenuItem>
            ))}
          </ParamDropdown>
          <ParamDropdown
            icon={<Gem size={12} />}
            label={quality === 'high' ? '高清画质' : '标准画质'}
            open={menu === 'quality'}
            onToggle={() => setMenu(menu === 'quality' ? '' : 'quality')}
            menuWidth={148}
          >
            <ParamMenuItem active={quality === 'standard'} onClick={() => { setQuality('standard'); setMenu(''); }}>
              标准画质
            </ParamMenuItem>
            <ParamMenuItem active={quality === 'high'} onClick={() => { setQuality('high'); setMenu(''); }}>
              高清画质
            </ParamMenuItem>
          </ParamDropdown>
          <ParamDropdown
            icon={<Monitor size={12} />}
            label={resolution.toUpperCase()}
            open={menu === 'resolution'}
            onToggle={() => setMenu(menu === 'resolution' ? '' : 'resolution')}
            menuWidth={132}
          >
            {['1k', '2k', '4k'].map((r) => (
              <ParamMenuItem key={r} active={r === resolution} onClick={() => { setResolution(r); setMenu(''); }}>
                {r.toUpperCase()}
              </ParamMenuItem>
            ))}
          </ParamDropdown>
          {/* 「自适应」对齐参考图展示（尺寸自适应/固定画幅；无对应生成维度，不透传请求） */}
          <ParamDropdown
            icon={<Scan size={12} />}
            label={adaptive}
            open={menu === 'adaptive'}
            onToggle={() => setMenu(menu === 'adaptive' ? '' : 'adaptive')}
            menuWidth={148}
          >
            <ParamMenuItem active={adaptive === '自适应'} onClick={() => { setAdaptive('自适应'); setMenu(''); }}>
              自适应
            </ParamMenuItem>
            <ParamMenuItem active={adaptive === '固定画幅'} onClick={() => { setAdaptive('固定画幅'); setMenu(''); }}>
              固定画幅
            </ParamMenuItem>
          </ParamDropdown>
          <ParamDropdown
            icon={<RectangleHorizontal size={12} />}
            label={ratio}
            open={menu === 'ratio'}
            onToggle={() => setMenu(menu === 'ratio' ? '' : 'ratio')}
            menuWidth={160}
          >
            {IMAGE_ASPECT_RATIOS.map((r) => (
              <ParamMenuItem key={r} active={r === ratio} onClick={() => { setRatio(r); setMenu(''); }}>
                {r}
              </ParamMenuItem>
            ))}
          </ParamDropdown>
        </div>
        <ConfirmButton disabled={selectedCount === 0} onClick={handleConfirm}>
          确认并创建生成队列 ({selectedCount})
        </ConfirmButton>
      </div>
    </ModalShell>
  );
}

/* ------------------------------------------------------------------ */
/* 图二「批量生视频」确认弹窗                                           */
/* ------------------------------------------------------------------ */

/** 行尾时长档位（默认 8s） */
const VIDEO_DURATION_OPTIONS = ['4s', '8s', '12s'];
/** 视频分辨率档位（对齐参考图「720P」） */
const VIDEO_RESOLUTION_OPTIONS = ['480P', '720P', '1080P'];

interface BatchVideoModalProps {
  /** 已出图的分镜行（ScriptNodeToolbar 构建并映射视频运动提示词） */
  items: BatchVideoItem[];
  onClose: () => void;
  /** 确认创建：携带所选分镜图节点 id（保持创建顺序）+ 视频参数/时长/运动提示词 */
  onConfirm: (selectedNodeIds: string[], options: BatchVideoOptions) => void;
}

export function BatchVideoModal({ items, onClose, onConfirm }: BatchVideoModalProps) {
  // 勾选集合存分镜图节点 id，默认全选
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(items.map((i) => i.nodeId)));
  // 每行时长（'8s' 等档位字符串，确认时转数字秒）
  const [durMap, setDurMap] = useState<Record<string, string>>(() =>
    Object.fromEntries(items.map((i) => [i.nodeId, '8s'])),
  );
  // v1.4.94 折叠面板（对齐批量生图 v1.4.87 同构）：当前展开提示词详情的行 id（单开互斥）
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [modelId, setModelId] = useState('');
  const [audio, setAudio] = useState(true);
  const [resolution, setResolution] = useState('720P');

  // 视频模型列表（文生/图生视频能力并集 + 供应商可见性；空列表走节点默认模型）
  useEnsureModels();
  const storeModels = useModelStore((s) => s.models);
  const providerVisibility = useModelStore((s) => s.providerVisibility);
  const videoModels = useMemo(
    () =>
      storeModels.filter(
        (m) =>
          (m.supportedModes.includes('txt2video') ||
            m.supportedModes.includes('img2video') ||
            m.supportedModes.includes('omni')) &&
          providerVisibility[m.backend] !== false,
      ),
    [storeModels, providerVisibility],
  );
  useFallbackModelId(videoModels, modelId, setModelId);
  const genModel = videoModels.find((m) => m.id === modelId) ?? videoModels[0];

  const { menu, setMenu, menuMask } = useDropdownMenu();

  const selectedCount = items.filter((i) => selectedIds.has(i.nodeId)).length;

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(selectedCount === items.length ? new Set() : new Set(items.map((i) => i.nodeId)));
  };

  const setDuration = (nodeId: string, value: string) => {
    setDurMap((prev) => ({ ...prev, [nodeId]: value }));
  };

  const handleConfirm = () => {
    if (selectedCount === 0) return;
    const durations: Record<string, number> = {};
    const prompts: Record<string, string> = {};
    for (const item of items) {
      if (!selectedIds.has(item.nodeId)) continue;
      const sec = parseInt(durMap[item.nodeId] ?? '8', 10);
      if (Number.isFinite(sec)) durations[item.nodeId] = sec;
      const mp = item.motionPrompt.trim();
      if (mp) prompts[item.nodeId] = mp;
    }
    onConfirm(
      items.filter((i) => selectedIds.has(i.nodeId)).map((i) => i.nodeId),
      {
        modelId: modelId || genModel?.id || '',
        generateAudio: audio,
        resolution,
        durations,
        prompts,
      },
    );
  };

  return (
    <ModalShell
      title="批量生视频"
      infoText="会优先使用已生成的角色、场景和道具参考图，让视频更贴合分镜内容。"
      onClose={onClose}
    >
      {/* 分镜勾选列表（v1.4.95 对齐批量生图行样式：行文本 = 画面描述语句，点击展开/折叠
          运动提示词全文——固定高度窗口超限滚动（视频提示词较长），行尾时长下拉独立不冲突） */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-3 pb-3 flex flex-col gap-2">
        {(() => {
          const idxLabel = items.map((i) => i.shotIndex).filter((n) => n > 0).join('、');
          return idxLabel ? (
            <div className="text-[12px] text-white/45">当前镜头 {idxLabel} 已出图，将按所选时长逐个创建视频生成节点</div>
          ) : null;
        })()}
        {items.map((item) => {
          const mp = item.motionPrompt.trim();
          // v1.4.95 未出图资产置灰（@名 灰显仍可 hover 查看卡片），同批量生图弹窗口径
          const mutedNames = item.assets.filter((a) => !a.imageUrl).map((a) => a.name);
          const expanded = expandedId === item.nodeId;
          return (
            <ShotRow
              key={item.nodeId}
              checked={selectedIds.has(item.nodeId)}
              onToggle={() => toggleItem(item.nodeId)}
              index={item.shotIndex}
              /* v1.4.95 行摘要 = 画面描述（对齐批量生图行语义；空时回退运动提示词摘要） */
              text={item.description || mp || '暂无画面描述'}
              expandable
              expanded={expanded}
              onToggleExpand={() => setExpandedId(expanded ? null : item.nodeId)}
              trailing={
                /* v1.4.93 行尾时长下拉复用 ParamDropdown（portal 菜单）：原 absolute 菜单
                 * 嵌在 overflow-y-auto 镜头列表内会被滚动区裁剪，compact+右对齐同款视觉 */
                <ParamDropdown
                  compact
                  align="right"
                  label={durMap[item.nodeId] ?? '8s'}
                  open={menu === `dur:${item.nodeId}`}
                  onToggle={() => setMenu(menu === `dur:${item.nodeId}` ? '' : `dur:${item.nodeId}`)}
                  menuWidth={120}
                >
                  {VIDEO_DURATION_OPTIONS.map((d) => (
                    <ParamMenuItem
                      key={d}
                      active={d === (durMap[item.nodeId] ?? '8s')}
                      onClick={() => { setDuration(item.nodeId, d); setMenu(''); }}
                    >
                      {d}
                    </ParamMenuItem>
                  ))}
                </ParamDropdown>
              }
            >
              {/* v1.4.95 展开块 = 运动提示词全文经 AssetMentionText 渲染（@资产 蓝色高亮 +
                  hover 264px 缩略图预览卡；预览卡 fixed z-400 按鼠标视口坐标定位，不受滚动
                  容器裁剪，同生图弹窗滚动列表先例；未出图资产置灰）：固定高度窗口
                  （max-h-40）+ 超限滚动，whitespace-pre-wrap 保留时间段分段 */}
              {expanded && (
                <div className="px-3 pb-2.5 pl-9">
                  <div className="max-h-40 overflow-y-auto">
                    <AssetMentionText
                      text={mp || '暂无视频运动提示词，将按分镜画面直接生成'}
                      assets={item.assets}
                      mutedNames={mutedNames}
                      className="text-[12px] leading-relaxed text-white/60 whitespace-pre-wrap break-words"
                    />
                  </div>
                </div>
              )}
            </ShotRow>
          );
        })}
        {items.length === 0 && (
          <div className="py-12 text-center text-[13px] text-white/35">暂无已出图的分镜</div>
        )}
      </div>
      {/* 底栏：已选计数 + 视频模型 + Audio 开关 + 分辨率 + 确认按钮（对齐参考图二；flex-wrap 兜底防小屏重叠） */}
      <div className="shrink-0 flex flex-wrap items-center gap-x-3 gap-y-2 px-6 py-3 bg-[#1c1c1e] border-t border-white/[0.06]">
        <SelectedCounter total={items.length} selectedCount={selectedCount} onToggleAll={toggleAll} />
        <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
          {menuMask}
          <ParamDropdown
            icon={<Cpu size={12} />}
            label={genModel?.name ?? '默认模型'}
            open={menu === 'model'}
            onToggle={() => setMenu(menu === 'model' ? '' : 'model')}
            menuWidth={216}
          >
            {videoModels.map((m) => (
              <ParamMenuItem key={m.id} active={m.id === modelId} onClick={() => { setModelId(m.id); setMenu(''); }}>
                {m.name}
              </ParamMenuItem>
            ))}
            {videoModels.length === 0 && (
              <div className="px-3 py-2 text-[12px] text-white/40">暂无可用模型，将使用默认视频模型</div>
            )}
          </ParamDropdown>
          {/* Audio 开关（对齐参考图二：音量 icon + 文案 + 滑块开关） */}
          <button
            type="button"
            onClick={() => setAudio((v) => !v)}
            title="生成视频是否携带音频"
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-white/10 bg-white/[0.06] text-[12px] text-white/70 hover:bg-white/10 transition-colors shrink-0"
          >
            <Volume2 size={12} className="text-white/45" />
            <span>Audio</span>
            <span className={`relative w-7 h-4 rounded-full transition-colors ${audio ? 'bg-blue-500' : 'bg-white/20'}`}>
              <span
                className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${audio ? 'left-[14px]' : 'left-0.5'}`}
              />
            </span>
          </button>
          <ParamDropdown
            icon={<Monitor size={12} />}
            label={resolution}
            open={menu === 'resolution'}
            onToggle={() => setMenu(menu === 'resolution' ? '' : 'resolution')}
            menuWidth={148}
          >
            {VIDEO_RESOLUTION_OPTIONS.map((r) => (
              <ParamMenuItem key={r} active={r === resolution} onClick={() => { setResolution(r); setMenu(''); }}>
                {r}
              </ParamMenuItem>
            ))}
          </ParamDropdown>
        </div>
        <ConfirmButton disabled={selectedCount === 0} onClick={handleConfirm}>
          确认并创建视频生成队列 ({selectedCount})
        </ConfirmButton>
      </div>
    </ModalShell>
  );
}
